import React, { forwardRef, useEffect, useState } from 'react'
import { Form, Input, Select, DatePicker, Button, Tag, message } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import './BookingForm.css';

// 設定 dayjs 為繁體中文
dayjs.locale('zh-tw');

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const MAX_BOOKING_DAYS = 7;

const AddForm = forwardRef((props, ref) => {
  // 添加狀態來追蹤日期範圍
  const [dateRange, setDateRange] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // 生成時間選項（每30分鐘一個選項）
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push({
          value: timeStr,
          label: timeStr
        });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  // 快速日期選項
  const getQuickDateOptions = () => [
    { label: '今天', value: 0 },
    { label: '到明天', value: 1 },
    { label: '到後天', value: 2 },
    { label: '一整週', value: 6 }
  ];

  // 快速時間段選項（4格 grid）
  const getQuickTimeOptions = () => [
    { label: '8~10點', start: '08:00', end: '10:00' },
    { label: '10~12點', start: '10:00', end: '12:00' },
    { label: '13:30~15點', start: '13:30', end: '15:00' },
    { label: '15~17點', start: '15:00', end: '17:00' },
  ];

  // 時段選項（上半天/下半天/全天）
  const getDayPeriodOptions = () => [
    { label: '上半天', start: '08:30', end: '12:00' },
    { label: '下半天', start: '13:30', end: '18:00' },
    { label: '全天', start: '08:30', end: '18:00' }
  ];

  // 格式化日期顯示（用於日期卡片）
  const formatDateDisplay = (date) => {
    if (!date) return '';
    const days = ['日', '一', '二', '三', '四', '五', '六'];
    return `${date.month() + 1}/${date.date()} (${days[date.day()]})`;
  };

  // 計算天數差
  const getDaysDiff = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return '';
    const diff = dateRange[1].diff(dateRange[0], 'day');
    return diff === 0 ? '當天' : `${diff + 1}天`;
  };

  // 檢查日期範圍是否超過上限
  const checkDateRangeLimit = (start, end) => {
    if (!start || !end) return true;
    const diff = end.diff(start, 'day');
    return diff < MAX_BOOKING_DAYS;
  };

  // 延長日期
  const extendDate = (days) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;
    const maxEnd = dateRange[0].add(MAX_BOOKING_DAYS - 1, 'day');
    // 已經到上限，無法再延長
    if (dateRange[1].isSame(maxEnd, 'day') || dateRange[1].isAfter(maxEnd, 'day')) {
      message.warning(`單次預約最多 ${MAX_BOOKING_DAYS} 天（一週），無法再延長`);
      return;
    }
    let newEnd = dateRange[1].add(days, 'day');
    // 超過上限時自動截到最大日期
    if (newEnd.isAfter(maxEnd, 'day')) {
      newEnd = maxEnd;
      message.info(`已自動調整到最大範圍（${MAX_BOOKING_DAYS} 天）`);
    }
    const newDateRange = [dateRange[0], newEnd];
    setDateRange(newDateRange);
    if (ref?.current) {
      ref.current.setFieldsValue({ dateRange: newDateRange });
      ref.current.validateFields(['dateRange']).catch(() => {});
    }
  };

  // 快速選擇日期
  const handleQuickDateSelect = (days) => {
    const startDate = dayjs(); // 今天
    let endDate;

    if (days === 0) {
      // 今天：開始和結束都是今天
      endDate = dayjs();
    } else {
      // 其他：今天 + days 天
      endDate = dayjs().add(days, 'day');
    }

    const newDateRange = [startDate, endDate];

    // 更新 state
    setDateRange(newDateRange);

    if (ref?.current) {

      ref.current.setFieldsValue({
        dateRange: newDateRange
      });

      // 觸發表單驗證和重新渲染
      ref.current.validateFields(['dateRange']).then(() => {
        // 驗證成功
      }).catch((err) => {
        // 驗證失敗
      });
    }
  };

  // 快速選擇時間
  const handleQuickTimeSelect = (timeOption) => {
    if (ref?.current) {
      ref.current.setFieldsValue({
        startTime: timeOption.start,
        endTime: timeOption.end
      });
      // 觸發表單驗證
      ref.current.validateFields(['startTime', 'endTime']);
    }
  };

  // 檢查時間段是否已被預約
  const isTimeSlotBooked = (start, end) => {
    // TODO: 實際檢查邏輯，需要結合 props.existingBookings 或類似數據
    return false;
  };

  // 自定義驗證器：檢查時間邏輯
  const validateDateTime = () => ({
    validator(_, value) {
      return new Promise((resolve, reject) => {
        const form = ref?.current;
        if (!form) {
          resolve();
          return;
        }

        const dateRange = form.getFieldValue('dateRange');
        const startTime = form.getFieldValue('startTime');
        const endTime = form.getFieldValue('endTime');

        // 如果有任何值缺失，跳過驗證（讓 required 規則處理）
        if (!dateRange || !startTime || !endTime || !dateRange[0] || !dateRange[1]) {
          resolve();
          return;
        }

        const startDate = dayjs(dateRange[0]).format('YYYY-MM-DD');
        const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD');
        const startDateTime = dayjs(`${startDate} ${startTime}`);
        const endDateTime = dayjs(`${endDate} ${endTime}`);

        if (endDateTime.isBefore(startDateTime) || endDateTime.isSame(startDateTime)) {
          reject(new Error('結束時間必須晚於開始時間'));
        } else {
          resolve();
        }
      });
    },
  });

  useEffect(() => {
    // 使用 setTimeout 確保 form 已經完全渲染
    const timer = setTimeout(() => {
      if (ref && ref.current) {

        // 如果有傳入的選定日期，使用該日期
        if (props.selectedDate && props.selectedDate.start_datetime) {
          const startDate = dayjs(props.selectedDate.start_datetime);
          const endDate = dayjs(props.selectedDate.end_datetime);
          const initialDateRange = [startDate, endDate];

          setDateRange(initialDateRange);
          ref.current.setFieldsValue({
            dateRange: initialDateRange,
            startTime: startDate.format('HH:mm'),
            endTime: endDate.format('HH:mm')
          });
        } else {
          // 如果沒有傳入日期，設定預設值為今天
          const today = dayjs();
          const defaultDateRange = [today, today]; // 今天到今天

          setDateRange(defaultDateRange);
          ref.current.setFieldsValue({
            dateRange: defaultDateRange,
            startTime: '08:00',
            endTime: '10:00'
          });
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [ref, props.selectedDate]); // 添加 props.selectedDate 作為依賴

  return (
    <Form
      layout="vertical"
      ref={ref}
    >
      <Form.Item
        name="booking_item_id"
        label="預約項目"
        initialValue={1}
        rules={[{ required: true, message: '請選擇預約項目' }]}
      >
        <Select
          style={{ width: '100%' }}
          showSearch
          placeholder="select a item"
          optionFilterProp="children"
          options={props.bookingItems.filter((item) => item.enable)}
          fieldNames={{
            label: 'name',
            value: 'id'
          }}
        />
      </Form.Item>

      <Form.Item
        name="dateRange"
        label="日期範圍"
        rules={[
          { required: true, message: '請選擇日期範圍' },
          {
            validator(_, value) {
              if (!value || !value[0] || !value[1]) return Promise.resolve();
              const diff = value[1].diff(value[0], 'day');
              if (diff >= MAX_BOOKING_DAYS) {
                return Promise.reject(new Error(`單次預約最多 ${MAX_BOOKING_DAYS} 天（一週）`));
              }
              return Promise.resolve();
            }
          }
        ]}
      >
        {/* 快速日期選擇按鈕 */}
        <div className="booking-quick-date-btns">
          {getQuickDateOptions().map((option, index) => (
            <Button
              key={index}
              className="booking-quick-date-btn"
              onClick={() => handleQuickDateSelect(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* 日期顯示卡片 */}
        {dateRange && dateRange[0] && dateRange[1] && (
          <div className="booking-date-card">
            <div className="booking-date-card-inner">
              <div className="booking-date-card-section">
                <div className="booking-date-card-label">開始</div>
                <div className="booking-date-card-value">{formatDateDisplay(dateRange[0])}</div>
                <div className="booking-date-card-year">{dateRange[0].year()}</div>
              </div>
              <div className="booking-date-card-arrow">
                <span className="booking-date-card-arrow-icon">→</span>
                <span className="booking-date-card-days">{getDaysDiff()}</span>
              </div>
              <div className="booking-date-card-section end">
                <div className="booking-date-card-label">結束</div>
                <div className="booking-date-card-value">{formatDateDisplay(dateRange[1])}</div>
                <div className="booking-date-card-year">{dateRange[1].year()}</div>
              </div>
            </div>
          </div>
        )}

        {/* 延長按鈕 + 自訂日期 */}
        <div className="booking-date-adjust-row">
          <div className="booking-date-adjust-left">
            <Button className="booking-extend-btn" onClick={() => extendDate(1)}>
              延長 +1天
            </Button>
            <Button className="booking-extend-btn" onClick={() => extendDate(6)}>
              延長 +6天
            </Button>
          </div>
          <Button
            className="booking-custom-date-btn"
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            <CalendarOutlined />
            自訂日期
          </Button>
        </div>

        {/* 自訂日期 RangePicker（收合狀態） */}
        {showDatePicker && (
          <div className="booking-custom-datepicker">
            <RangePicker
              style={{ width: '100%' }}
              format={(value) => value.format('YYYY-MM-DD (ddd)')}
              placeholder={['開始日期', '結束日期']}
              value={dateRange}
              showToday={true}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1] && !checkDateRangeLimit(dates[0], dates[1])) {
                  message.warning(`單次預約最多 ${MAX_BOOKING_DAYS} 天（一週），請重新選擇`);
                  return;
                }
                setDateRange(dates);
                if (ref?.current) {
                  ref.current.setFieldsValue({
                    dateRange: dates
                  });
                  ref.current.validateFields(['dateRange']).catch(() => {});
                }
              }}
            />
          </div>
        )}
      </Form.Item>

      <Form.Item label="時間範圍" style={{ marginBottom: '16px' }} required>
        {/* 快速時段選擇 - 4 欄 grid */}
        <div className="booking-quick-time-grid">
          {getQuickTimeOptions().map((option, index) => {
            const isBooked = isTimeSlotBooked(option.start, option.end);
            return (
              <Button
                key={index}
                className="booking-quick-time-btn"
                disabled={isBooked}
                onClick={() => handleQuickTimeSelect(option)}
              >
                {option.label}
                {isBooked && <Tag color="red" style={{ marginLeft: '4px', fontSize: '10px' }}>已預約</Tag>}
              </Button>
            );
          })}
        </div>

        {/* 時段選擇 - 上半天/下半天/全天 */}
        <div className="booking-day-period-btns">
          {getDayPeriodOptions().map((option, index) => (
            <Button
              key={index}
              className="booking-quick-time-btn"
              onClick={() => handleQuickTimeSelect(option)}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* 時間選擇器 - 紫色容器 */}
        <div className="booking-time-container">
          <Form.Item
            name="startTime"
            rules={[
              { required: true, message: '請選擇開始時間' },
              validateDateTime
            ]}
            style={{ marginBottom: 0 }}
          >
            <Select
              style={{ width: '100%' }}
              placeholder="開始時間"
              options={timeOptions}
              showSearch
            />
          </Form.Item>
          <span className="booking-time-separator">至</span>
          <Form.Item
            name="endTime"
            rules={[
              { required: true, message: '請選擇結束時間' },
              validateDateTime
            ]}
            style={{ marginBottom: 0 }}
          >
            <Select
              style={{ width: '100%' }}
              placeholder="結束時間"
              options={timeOptions}
              showSearch
            />
          </Form.Item>
        </div>
      </Form.Item>

      <Form.Item
        name="remark"
        label="備註"
        style={{ marginTop: '8px' }}
      >
        <TextArea rows={3} placeholder="輸入備註事項..." />
      </Form.Item>
    </Form>
  )
})
export default AddForm
